import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { BadgeCheck, Link2, ShoppingBag, ArrowRight } from "lucide-react";

/**
 * /selger – hub for verifiserte selgere.
 * Vises kun for brukere med `seller_verified=true`.
 * Uverifiserte sendes til /innstillinger/selger-verifisering.
 */
export default function SelgerHub() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(false);

  useEffect(() => {
    if (!user?.seller_verified) return;
    let cancel = false;
    (async () => {
      try {
        // Best-effort: hent salgs-oppsummering hvis endepunkt finnes.
        const r = await api.get("/me/seller/summary").catch(() => null);
        if (!cancel) {
          if (r?.data) setStats(r.data);
          else setStatsErr(true);
        }
      } catch {
        if (!cancel) setStatsErr(true);
      }
    })();
    return () => { cancel = true; };
  }, [user?.seller_verified]);

  if (loading) return <Layout><div className="p-8 text-[var(--cream-soft)]">Laster…</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.seller_verified) return <Navigate to="/innstillinger/selger-verifisering" replace />;

  const sold = stats?.items_sold_total ?? 0;
  const earned30 = stats?.revenue_30d ?? null;
  const earnedTotal = stats?.revenue_total ?? null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <BadgeCheck className="h-8 w-8 text-emerald-400" />
          <div>
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Selger</h1>
            <p className="text-xs text-emerald-400 font-meta uppercase tracking-widest">Verifisert selger-status aktiv</p>
          </div>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mb-8">
          Oversikt og administrasjon av selger-funksjonene dine.
        </p>

        {/* Salgs-statistikk – vises kun hvis stats-endepunktet ga data */}
        {stats && !statsErr && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <div className="panel-tight p-4">
              <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Solgte elementer</div>
              <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{sold}</div>
            </div>
            {earned30 !== null && (
              <div className="panel-tight p-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Inntekt siste 30 d</div>
                <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{earned30} chips</div>
              </div>
            )}
            {earnedTotal !== null && (
              <div className="panel-tight p-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Total inntekt</div>
                <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{earnedTotal} chips</div>
              </div>
            )}
          </div>
        )}

        {/* Kort: selger-lenker */}
        <Link
          to="/innstillinger/selger-lenker"
          className="panel-tight p-5 mb-3 flex items-center gap-4 hover:border-[var(--magenta)] transition group border border-white/10"
          data-testid="card-selger-lenker"
        >
          <Link2 className="h-6 w-6 text-[var(--magenta)] shrink-0" />
          <div className="flex-1">
            <div className="font-fraunces italic text-xl text-[var(--cream)]">Administrer selger-lenker</div>
            <div className="text-xs text-[var(--cream-soft)]">Legg til / oppdater lenker til OnlyFans, Fansly, egen shop m.m.</div>
          </div>
          <ArrowRight className="h-5 w-5 text-white/40 group-hover:text-[var(--magenta)] transition" />
        </Link>

        {/* Kort: solgte elementer (kun hvis solgt > 0) */}
        {sold > 0 && (
          <Link
            to="/skaper-studio"
            className="panel-tight p-5 mb-3 flex items-center gap-4 hover:border-[var(--magenta)] transition group border border-white/10"
            data-testid="card-solgte-elementer"
          >
            <ShoppingBag className="h-6 w-6 text-[var(--magenta)] shrink-0" />
            <div className="flex-1">
              <div className="font-fraunces italic text-xl text-[var(--cream)]">Solgte elementer</div>
              <div className="text-xs text-[var(--cream-soft)]">Full oversikt over PPV-låst innhold og salg.</div>
            </div>
            <ArrowRight className="h-5 w-5 text-white/40 group-hover:text-[var(--magenta)] transition" />
          </Link>
        )}

        <p className="text-[10px] text-[var(--cream-faint)] mt-8">
          Verifisert via BankID/StrexId. Selger-status er permanent så lenge kontoen er i god stand.
        </p>
      </div>
    </Layout>
  );
}
